import { useState } from "react";
import { useAllProviderEvaluations, calculateAverageScore } from "@/hooks/useShiftaskProviderEvaluation";
import { useClientProfiles, getClientDisplayName } from "@/hooks/useClientProfiles";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  BarChart3, 
  Activity, 
  Star, 
  TrendingUp, 
  ThumbsUp, 
  Shield,
  PieChart,
  Users,
  Clock,
  AlertTriangle,
  Laptop,
  DollarSign,
  Download
} from "lucide-react";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { generateProviderEvaluationPDF } from "@/utils/generateProviderEvaluationPDF";
import { useAdminCustomization } from "@/hooks/useAdminCustomization";
import { toast } from "sonner";

const metrics = [
  { key: 'response_time', icon: Clock },
  { key: 'incident_handling', icon: AlertTriangle },
  { key: 'guard_quality', icon: Shield },
  { key: 'professionalism', icon: Users },
  { key: 'communication', icon: Activity },
  { key: 'technology_satisfaction', icon: Laptop },
  { key: 'reporting_quality', icon: BarChart3 },
  { key: 'innovation', icon: TrendingUp },
  { key: 'value_for_money', icon: DollarSign },
  { key: 'contract_flexibility', icon: Shield },
  { key: 'account_management', icon: Users },
];

export const AdminAnalytics = () => {
  const { t } = useLanguage();
  const { data: evaluations = [] } = useAllProviderEvaluations();
  const [selectedClient, setSelectedClient] = useState<string>("all");
  const { customization } = useAdminCustomization();

  // Get unique clients
  const clients = Array.from(new Set(evaluations.map(e => e.client_id)));
  
  // Fetch client profiles
  const { data: clientProfiles = {} } = useClientProfiles(clients);

  const handleDownloadPDF = async (evaluation: any) => {
    try {
      const clientName = getClientDisplayName(clientProfiles[evaluation.client_id]);
      const appName = customization?.app_name || 'OVIT';
      await generateProviderEvaluationPDF(evaluation, clientName, appName, false);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF');
    }
  };

  // Filter evaluations based on selected client
  const filteredEvaluations = selectedClient === "all" 
    ? evaluations 
    : evaluations.filter(e => e.client_id === selectedClient);

  // Helper function to get metric label
  const getMetricLabel = (metricKey: string) => {
    const labelMap: Record<string, string> = {
      'response_time': t('responseTime'),
      'incident_handling': t('incidentHandling'),
      'guard_quality': t('guardQuality'),
      'professionalism': t('professionalism'),
      'communication': t('communication'),
      'technology_satisfaction': t('technologySatisfaction'),
      'reporting_quality': t('reportingQuality'),
      'innovation': t('innovation'),
      'value_for_money': t('valueForMoney'),
      'contract_flexibility': t('contractFlexibility'),
      'account_management': t('accountManagement'),
    };
    return labelMap[metricKey] || metricKey;
  };

  // Calculate overall rating
  const overallRating = filteredEvaluations.length > 0
    ? filteredEvaluations.reduce((sum, e) => sum + calculateAverageScore(e), 0) / filteredEvaluations.length
    : 0;

  // Calculate NPS
  const npsScores = filteredEvaluations.map(e => e.overall_satisfaction);
  const promoters = npsScores.filter(s => s >= 9).length;
  const passives = npsScores.filter(s => s >= 7 && s < 9).length;
  const detractors = npsScores.filter(s => s < 7).length;
  const npsScore = npsScores.length > 0 
    ? Math.round(((promoters - detractors) / npsScores.length) * 100)
    : 0;

  // Would recommend percentage
  const wouldRecommend = filteredEvaluations.length > 0
    ? (filteredEvaluations.filter(e => e.would_recommend).length / filteredEvaluations.length) * 100
    : 0;

  // Would renew percentage
  const wouldRenew = filteredEvaluations.length > 0
    ? (filteredEvaluations.filter(e => e.likely_to_renew).length / filteredEvaluations.length) * 100
    : 0;

  // Calculate metric averages
  const getMetricAverage = (metricKey: string) => {
    if (filteredEvaluations.length === 0) return 0;
    const scoreKey = `${metricKey}_score` as keyof typeof filteredEvaluations[0];
    return filteredEvaluations.reduce((sum, e) => sum + (e[scoreKey] as number || 0), 0) / filteredEvaluations.length;
  };

  // Get rating color
  const getRatingColor = (score: number) => {
    if (score >= 4.5) return "text-emerald-600 dark:text-emerald-400";
    if (score >= 3.5) return "text-blue-600 dark:text-blue-400";
    if (score >= 2.5) return "text-amber-600 dark:text-amber-400";
    return "text-red-600 dark:text-red-400";
  };

  const getRatingBg = (score: number) => {
    if (score >= 4.5) return "bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300";
    if (score >= 3.5) return "bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300";
    if (score >= 2.5) return "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300";
    return "bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300";
  };

  // Get NPS color
  const getNPSColor = (nps: number) => {
    if (nps > 50) return "text-emerald-600 dark:text-emerald-400";
    if (nps >= 0) return "text-amber-600 dark:text-amber-400";
    return "text-red-600 dark:text-red-400";
  };

  const getNPSBg = (nps: number) => {
    if (nps > 50) return "bg-emerald-100 dark:bg-emerald-950";
    if (nps >= 0) return "bg-amber-100 dark:bg-amber-950";
    return "bg-red-100 dark:bg-red-950";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-br from-background to-primary/5 border-primary/20">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-4 mb-2">
            <div className="bg-gradient-to-br from-primary to-primary/80 p-3 rounded-lg">
              <BarChart3 className="h-8 w-8 text-primary-foreground" />
            </div>
            <Activity className="h-6 w-6 text-primary animate-pulse" />
          </div>
          <CardTitle className="text-3xl bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            {t("customerEvaluationAnalytics")}
          </CardTitle>
          <CardDescription>
            {t("fullOverviewAndAnalysis")}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Client Filter Tabs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('filterByClient')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedClient} onValueChange={setSelectedClient}>
            <TabsList className="flex flex-wrap h-auto">
              <TabsTrigger value="all" className="gap-2">
                <Users className="h-4 w-4" />
                {t('allClients')} ({evaluations.length})
              </TabsTrigger>
              {clients.map(clientId => (
                <TabsTrigger key={clientId} value={clientId} className="gap-2">
                  <Shield className="h-4 w-4" />
                  {getClientDisplayName(clientProfiles[clientId])}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* Key Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Overall Rating */}
        <Card className="hover:shadow-lg transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("overallRating")}</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${getRatingColor(overallRating)}`}>
              {overallRating.toFixed(1)}/5
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t("averageOfAllMetrics")}
            </p>
          </CardContent>
        </Card>

        {/* NPS Score */}
        <Card className="hover:shadow-lg transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">NPS Score</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${getNPSColor(npsScore)}`}>
              {npsScore}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Net Promoter Score
            </p>
          </CardContent>
        </Card>

        {/* Would Recommend */}
        <Card className="hover:shadow-lg transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("wouldRecommend")}</CardTitle>
            <ThumbsUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-500">
              {wouldRecommend.toFixed(0)}%
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t("clientsWhoWouldRecommend")}
            </p>
          </CardContent>
        </Card>

        {/* Would Renew */}
        <Card className="hover:shadow-lg transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("wouldRenew")}</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">
              {wouldRenew.toFixed(0)}%
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t("clientsWhoWouldRenew")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* NPS Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="h-5 w-5" />
            {t("npsScoreBreakdown")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-emerald-500">Promoters (9-10)</span>
                <span className="text-sm font-medium">{promoters} {t("clientsLowercase")}</span>
              </div>
              <Progress value={(promoters / npsScores.length) * 100} className="bg-emerald-500/20 [&>div]:bg-emerald-500" />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-yellow-500">Passives (7-8)</span>
                <span className="text-sm font-medium">{passives} {t("clientsLowercase")}</span>
              </div>
              <Progress value={(passives / npsScores.length) * 100} className="bg-yellow-500/20 [&>div]:bg-yellow-500" />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-red-500">Detractors (0-6)</span>
                <span className="text-sm font-medium">{detractors} {t("clientsLowercase")}</span>
              </div>
              <Progress value={(detractors / npsScores.length) * 100} className="bg-red-500/20 [&>div]:bg-red-500" />
            </div>
          </div>
          
          <div className={`text-center p-4 rounded-lg ${getNPSBg(npsScore)}`}>
            <div className={`text-4xl font-bold ${getNPSColor(npsScore)}`}>{npsScore}</div>
            <p className="text-sm text-muted-foreground mt-1">Final NPS Score</p>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Service Quality */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary">
              <Shield className="h-5 w-5" />
              {t("serviceQuality")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {['response_time', 'incident_handling', 'guard_quality', 'professionalism', 'communication'].map(key => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm">{getMetricLabel(key)}</span>
                <Badge variant="secondary" className={getRatingBg(getMetricAverage(key))}>
                  {getMetricAverage(key).toFixed(1)}/5
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Technology & Innovation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-500">
              <Laptop className="h-5 w-5" />
              {t("technologyAndInnovation")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {['technology_satisfaction', 'reporting_quality', 'innovation'].map(key => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm">{getMetricLabel(key)}</span>
                <Badge variant="secondary" className={getRatingBg(getMetricAverage(key))}>
                  {getMetricAverage(key).toFixed(1)}/5
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Value & Relationship */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-500">
              <DollarSign className="h-5 w-5" />
              {t("valueAndRelationship")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {['value_for_money', 'contract_flexibility', 'account_management'].map(key => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm">{getMetricLabel(key)}</span>
                <Badge variant="secondary" className={getRatingBg(getMetricAverage(key))}>
                  {getMetricAverage(key).toFixed(1)}/5
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* All Evaluations Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("allEvaluations")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredEvaluations.map(evaluation => {
              const avgScore = calculateAverageScore(evaluation);
              return (
                <div key={evaluation.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="space-y-1">
                    <div className="font-medium">{getClientDisplayName(clientProfiles[evaluation.client_id])}</div>
                    <div className="text-sm text-muted-foreground">
                      {evaluation.evaluation_period} {evaluation.evaluation_year}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className={`text-lg font-bold ${getRatingColor(avgScore)}`}>
                        {avgScore.toFixed(1)}/5
                      </div>
                      <div className="text-sm text-muted-foreground">NPS: {evaluation.overall_satisfaction}</div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {format(new Date(evaluation.submitted_at), 'PPp', { locale: el })}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleDownloadPDF(evaluation)}>
                      <Download className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Qualitative Feedback */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* What Works Well */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-500">
              <ThumbsUp className="h-5 w-5" />
              {t("whatWorksWell")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {filteredEvaluations
              .filter(e => e.what_working_well)
              .map(evaluation => (
                <div key={evaluation.id} className="p-3 bg-emerald-100 dark:bg-emerald-950 rounded-lg">
                  <div className="text-sm font-medium mb-1 text-emerald-900 dark:text-emerald-100">
                    {getClientDisplayName(clientProfiles[evaluation.client_id])}
                  </div>
                  <div className="text-sm text-emerald-800 dark:text-emerald-200">{evaluation.what_working_well}</div>
                  <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                    {format(new Date(evaluation.submitted_at), 'PP', { locale: el })}
                  </div>
                </div>
              ))}
          </CardContent>
        </Card>

        {/* Areas for Improvement */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-500">
              <AlertTriangle className="h-5 w-5" />
              Περιοχές Βελτίωσης
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {filteredEvaluations
              .filter(e => e.improvement_areas)
              .map(evaluation => (
                <div key={evaluation.id} className="p-3 bg-orange-100 dark:bg-orange-950 rounded-lg">
                  <div className="text-sm font-medium mb-1 text-orange-900 dark:text-orange-100">
                    {getClientDisplayName(clientProfiles[evaluation.client_id])}
                  </div>
                  <div className="text-sm text-orange-800 dark:text-orange-200">{evaluation.improvement_areas}</div>
                  <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    {format(new Date(evaluation.submitted_at), 'PP', { locale: el })}
                  </div>
                </div>
              ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
